import type { ReactNode, Ref } from "react";
import type { CoachPatientHeader as Header } from "@care-by-ella/contracts";
import { coachHeaderCells } from "@/lib/coach-header";

// TES-1074 point 1 — the coach's persistent patient header.
//
// The numbers a coach needs before anything else, above all three levels and
// pinned while she scrolls on a wide screen. Same visual grammar as the
// doctor's PatientHeader (one white card, ink values over sentence-case
// labels) but its own component: the doctor's reads PatientDetail, which a
// coach never receives.
//
// A missing value keeps its cell and says what is missing; the cell is muted,
// never red. BMI carries its caveat in the cell itself, not in a tooltip.
export function CoachPatientHeader({
  title,
  header,
  ref,
}: {
  /** Name, badges and the back link, rendered above the numbers. */
  title: ReactNode;
  /** Absent on an older API; the card then shows the title only. */
  header: Header | undefined;
  /** For the page to measure, so its tab bar pins right under the header. */
  ref?: Ref<HTMLElement>;
}) {
  return (
    <section
      ref={ref}
      aria-label="Patientöversikt"
      className="rounded-lg border border-surfaceMuted bg-surface px-md pt-md pb-md lg:sticky lg:top-0 lg:z-10 lg:px-lg"
    >
      {title}
      {header ? (
        <dl className="mt-md grid grid-cols-2 gap-md border-t border-surfaceMuted pt-md sm:grid-cols-4">
          {coachHeaderCells(header).map((cell) => (
            <div key={cell.key} className="flex min-w-0 flex-col gap-[2px]">
              <dt className="text-xs text-textSecondary">{cell.label}</dt>
              <dd
                className={`font-display text-lg leading-6 ${cell.missing ? "text-textSecondary" : "text-textPrimary"}`}
              >
                {cell.value}
                {/* TES-1333 — the status, when it adds anything, is a small
                    badge after the value, never the value itself. */}
                {cell.badge ? (
                  <span className="ml-xs inline-block rounded-pill border border-surfaceMuted px-xs align-middle font-sans text-[11px] leading-4 text-textSecondary">
                    {cell.badge}
                  </span>
                ) : null}
              </dd>
              {cell.detail ? (
                <dd className="text-xs text-textSecondary">{cell.detail}</dd>
              ) : null}
            </div>
          ))}
        </dl>
      ) : null}
    </section>
  );
}
